// The gesture layer — the DOM half of the stroke recognizer (K-91f). It listens
// for pointer events over the mounted field, draws the person's INK while the
// finger is down, hit-tests which field objects the stroke crossed, hands the
// recorded trace to the PURE classifier (recognizer.ts), and on a recognised
// stroke posts it to the server (POST /challenge/{id}/stroke) via fetch with the
// HX-Request header so the server answers with the same fragment HTMX would swap.
//
// Framework-free (canon 91 §1): no SPA framework, no runtime dependency; just
// the DOM and fetch. It leans on the recognizer for all classification so the
// geometry logic stays pure and testable; this module owns only the DOM.
//
// Canon 83 §1.4 — NO hand, finger, cursor or body is ever drawn. While the
// finger is down the island draws the raw INK the person is drawing (a path that
// follows the pointer); on release it SMOOTHS that ink to the logical shape (the
// settle of file 77 §3 / file 83 §1.1) — a straight line for a connect, a closed
// ring for an encircle — then the ink fades. The pointer leaves no cursor. The
// ink is a VERB (file 77 §4): its geometry is thrown away once the stroke is
// recognised; only the meaning (kind + touched set) is posted and kept.
//
// The island owns its own ink styling (a runtime-injected keyframe + inline
// styles) — it NEVER writes to web/css/field.css or tokens.css (the same
// contract electric.ts keeps). The ink uses the charge colour var(--live) with a
// literal fallback so it works before the page CSS loads and headless.

import {
  classify,
  crossTieFormBody,
  strokeFormBody,
  targetChallengeId,
  type Pane,
  type Recognised,
  type TouchedObject,
  type Trace,
  type TracePoint,
} from "./recognizer.js";
// ISLAND PR3 — the stroke ink settles through the island's ONE motion language
// (motion.ts). The ink's fade-out was a hand-rolled CSS `@keyframes
// objexiv-ink-fade` applied via element.style.animation; it now runs through
// motion.ts's easeTo (the same Motion One primitive the overlay and swap speak),
// so the field has one motion system, not two. prefers-reduced-motion is
// honoured in motion.ts once for the whole island: easeTo under reduced motion
// sets the end state (opacity 0) instantly and resolves, so the ink is removed
// with no animation — no island-owned stylesheet to inject for it.
import { easeTo } from "./motion.js";

const SVG_NS = "http://www.w3.org/2000/svg";

// The charge colour for the in-progress ink. var() with a literal fallback so it
// reads before the page CSS loads and in a headless capture (matches electric.ts).
const INK_COLOR = "var(--live, oklch(0.82 0.17 60))";

// ---- resolving a pointer position into the field SVG's user space -----------

/** Map a client (pixel) point into the SVG's own viewBox user space, so the
 *  trace the classifier reads is resolution-independent. Uses the SVG CTM; falls
 *  back to client coordinates when the matrix is unavailable (headless edge). */
function toUserSpace(svg: SVGSVGElement, clientX: number, clientY: number): TracePoint {
  const ctm = svg.getScreenCTM?.();
  if (!ctm) return { x: clientX, y: clientY };
  const pt = svg.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const inv = ctm.inverse();
  const u = pt.matrixTransform(inv);
  return { x: u.x, y: u.y };
}

// ---- hit-testing: which field object is under a client point ----------------

/** The pane an element sits in (file 75 §3). An element inside .pane-a / .pane-b
 *  is left / right; otherwise it is the single (non-split) feed surface. */
function paneOf(el: Element): Pane {
  if (el.closest(".pane-a")) return "left";
  if (el.closest(".pane-b")) return "right";
  return "feed";
}

/** The challenge id an element sits inside, from its enclosing
 *  #challenge-{id} section (the render layer's id). Null when outside any. */
function challengeIdOf(el: Element): string | null {
  const section = el.closest<HTMLElement>(".challenge-view");
  if (!section) return null;
  const id = section.id; // "challenge-{uuid}"
  return id.startsWith("challenge-") ? id.slice("challenge-".length) : null;
}

/** Resolve the addressable field object under a client point, or null. Takes are
 *  `li.take[data-take]`, keywords `li.keyword[data-word]`, a challenge ring the
 *  header svg inside a `.challenge-view`. Only objects the field renders as
 *  addressable are resolved; everything else is "touched nothing". */
function objectAt(doc: Document, clientX: number, clientY: number): TouchedObject | null {
  const el = doc.elementFromPoint(clientX, clientY);
  if (!el) return null;

  const take = el.closest<HTMLElement>("li.take[data-take]");
  if (take) {
    const id = take.getAttribute("data-take");
    if (id) return { kind: "take", id, pane: paneOf(take) };
  }

  const keyword = el.closest<HTMLElement>(".keyword[data-word]");
  if (keyword) {
    const word = keyword.getAttribute("data-word");
    const takeId = keyword.getAttribute("data-take");
    // A keyword object's id is the word scoped to its owning take, so two takes'
    // identical words are distinct objects (file 86).
    if (word) return { kind: "keyword", id: takeId ? `${takeId}:${word}` : word, pane: paneOf(keyword) };
  }

  // A challenge ring: the header svg (or the ring circle) inside a challenge
  // view. The challenge's id is the enclosing section's id.
  const header = el.closest<HTMLElement>(".challenge-view > header");
  if (header) {
    const id = challengeIdOf(header);
    if (id) return { kind: "challenge", id, pane: paneOf(header) };
  }

  return null;
}

/** The drawn line (a tie or a reading-member line) under a client point, for a
 *  scratch. Ties render as `li.tie[data-a][data-b]`; the scratch addresses the
 *  line, and whether the viewer made it decides cut vs weaken (file 77 §2). The
 *  render layer does not yet mark a tie's maker on the element, so `madeByMe` is
 *  conservatively false (weaken) until that datum is wired — the server treats an
 *  unknown line as weaken for a tie not made by the viewer, the safe reading. */
function lineAt(doc: Document, clientX: number, clientY: number): Trace["crossedLine"] {
  const el = doc.elementFromPoint(clientX, clientY);
  if (!el) return null;
  const tie = el.closest<HTMLElement>(".tie[data-a][data-b]");
  if (tie) {
    const mine = tie.getAttribute("data-mine") === "true";
    return { line: "tie", madeByMe: mine };
  }
  return null;
}

// ---- the in-progress ink (drawn while the finger is down; no cursor) --------

/**
 * The ink overlay: one SVG path that follows the pointer while the finger is
 * down (the raw ink the person draws), living in the field SVG so it shares the
 * viewBox. On release it is SMOOTHED to the logical shape and faded. It is NOT a
 * cursor (canon 83 §1.4) — it is the mark the person is making, which is a thing
 * the surface shows; the pointer itself is never drawn.
 */
class Ink {
  private path: SVGPathElement | null = null;
  constructor(private svg: SVGSVGElement) {}

  begin(p: TracePoint): void {
    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("class", "objexiv-ink");
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", INK_COLOR);
    path.setAttribute("stroke-width", "2.5");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");
    path.setAttribute("d", `M ${round2(p.x)} ${round2(p.y)}`);
    path.style.pointerEvents = "none";
    this.svg.appendChild(path);
    this.path = path;
  }

  extend(pts: TracePoint[]): void {
    if (!this.path || pts.length === 0) return;
    this.path.setAttribute("d", polyline(pts));
  }

  /** Smooth the ink to the stroke's logical shape and fade it out (the settle of
   *  file 77 §3). A connect/carry/pull smooths to a straight line between its
   *  endpoints; an encircle to a closed ring through the points; a scratch and a
   *  no-match simply fade the raw ink. */
  settle(pts: TracePoint[], recognised: Recognised | null): void {
    if (!this.path) return;
    if (recognised && pts.length >= 2) {
      if (recognised.stroke === "connect" || recognised.stroke === "carry" || recognised.stroke === "pull") {
        this.path.setAttribute("d", `M ${round2(pts[0].x)} ${round2(pts[0].y)} L ${round2(pts[pts.length - 1].x)} ${round2(pts[pts.length - 1].y)}`);
      } else if (recognised.stroke === "encircle") {
        this.path.setAttribute("d", `${polyline(pts)} Z`);
      }
    }
    this.fadeAndRemove();
  }

  /** On a no-match or a cancel, fade the raw ink with no smoothing (file 77 §3:
   *  "the ink fades over half a second and the objects settle"). */
  abandon(): void {
    this.fadeAndRemove();
  }

  /** Fade the ink to nothing through motion.ts (the island's one motion
   *  language), then remove the node. motion.ts owns prefers-reduced-motion:
   *  under it easeTo sets opacity 0 instantly and resolves, so the ink is
   *  removed with no travel — the correct no-motion settle (file 77 §3 / canon
   *  94 §7). The fade is a timed ease-out, the SAME tween the swap enter/exit
   *  uses, so a settling stroke reads in the one hand. */
  private fadeAndRemove(): void {
    const path = this.path;
    this.path = null;
    if (!path) return;
    // From the ink's drawn opacity down to gone, over half a second (file 77 §3).
    path.style.opacity = "0.9";
    easeTo(path, { opacity: 0 }, 500, [0, 0, 0.3, 1]).finished.then(() => path.remove());
    // Safety net where the animation's finished promise never settles (a
    // headless/unsupported edge): remove shortly after the fade would have ended.
    window.setTimeout(() => {
      if (path.isConnected) path.remove();
    }, 700);
  }
}

function polyline(pts: TracePoint[]): string {
  if (pts.length === 0) return "";
  let d = `M ${round2(pts[0].x)} ${round2(pts[0].y)}`;
  for (let i = 1; i < pts.length; i++) d += ` L ${round2(pts[i].x)} ${round2(pts[i].y)}`;
  return d;
}

function round2(f: number): number {
  return Math.round(f * 100) / 100;
}

// ---- dispatch: post a recognised stroke to the server -----------------------

/** Which pane maps to which challenge id, read from the live DOM. A single
 *  (non-split) surface's challenge is the one mounted challenge view; a split's
 *  two panes carry their own ids on the #split container (data-a / data-b). */
function paneChallenges(root: HTMLElement): Record<Pane, string | null> {
  const split = root.querySelector<HTMLElement>("#split[data-a]");
  if (split) {
    return {
      feed: null,
      left: split.getAttribute("data-a"),
      right: split.getAttribute("data-b"),
    };
  }
  const view = root.querySelector<HTMLElement>(".challenge-view");
  const id = view ? challengeIdOf(view) : null;
  return { feed: id, left: null, right: null };
}

/**
 * Post a recognised stroke to the server and swap the returned fragment into the
 * field, exactly as HTMX would (the HX-Request header makes the server answer
 * with a bare fragment). The body is the form the server reads (stroke + take
 * ids); NO geometry is sent (file 77 §5).
 *
 * CROSS-PANE (canon 75 §3): a stroke whose two ends sit in the two panes of a
 * split is the graph made by hand ACROSS challenges — a CROSS TIE (I-45). The
 * server route is POST /tie/cross: a `scope=cross` tie over the two take
 * placements in the two challenges, a PROPOSAL faint until the other author
 * accepts (internal/field.CrossTie). A cross-pane stroke is dispatched there
 * with {take×2, challenge×2} index-aligned to the two panes; on success the
 * settled ink is LEFT in place (the stroke already settled to the line between
 * the two endpoints in onUp) — the faint proposed cross tie is drawn on each
 * pane's overlay on its next read, so the person's gesture lands and stays, not
 * a blink. The server's /tie/cross answer is a plain proposal confirmation, not
 * field markup, so it is NOT swapped into #field (that would tear down the
 * split); the ink settling IS the surface's acknowledgement (file 77 §3).
 */
async function dispatch(
  root: HTMLElement,
  recognised: Recognised,
  crossedLine: Trace["crossedLine"],
): Promise<void> {
  // CROSS-PANE (canon 75 §3, I-45): the two ends sit in the two panes of the
  // split. Post the cross tie over the two placements/takes in the two
  // challenges and leave the settled ink — no field swap (that would tear down
  // the split). A non-connect/carry cross-pane stroke (e.g. a pull between two
  // challenge rings) is not a cross tie, so only an object→object stroke whose
  // two takes span the panes is dispatched; anything else leaves its settled ink
  // with no post, the same quiet "did nothing server-side" of a 204.
  if (recognised.crossPane) {
    await dispatchCrossTie(root, recognised);
    return;
  }

  const challengeId = targetChallengeId(recognised, paneChallenges(root));
  if (!challengeId) return;

  const body = strokeFormBody(recognised, crossedLine);
  let res: Response;
  try {
    res = await fetch(`/challenge/${challengeId}/stroke`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "HX-Request": "true",
      },
      body: body.toString(),
      credentials: "same-origin",
    });
  } catch {
    // A failed post leaves no mark — the surface does nothing (file 77 §2). The
    // ink has already settled/faded; there is nothing to roll back.
    return;
  }

  // 204 = the stroke meant nothing server-side, or the act is not wired yet: no
  // mark, no error the surface must explain (file 77 §2). A 401 = sign in to
  // take: the server returns the sign-in invitation fragment, which we swap so
  // the person sees it. A 2xx with a body is the re-rendered field.
  if (res.status === 204) return;
  const html = await res.text();
  if (!html) return;
  const field = root.querySelector<HTMLElement>("svg")?.closest("#field") ?? root;
  // The server returns the challenge fragment HTMX would swap into #field.
  (field as HTMLElement).innerHTML = html;
}

/**
 * Post a CROSS-PANE stroke to the cross-tie route (canon 75 §3 / file 77 §2
 * I-45): POST /tie/cross with the two takes and their two home challenges,
 * index-aligned (take=<a>&take=<b>&challenge=<chA>&challenge=<chB>), the exact
 * form internal/field.CrossTie reads. The two endpoints come from the recognised
 * stroke's objects (its first and last touched object, the origin and the
 * destination of an object→object drag); each object's pane resolves to that
 * pane's challenge via the live #split container.
 *
 * Only a stroke whose two ends are BOTH takes, one in each pane, is a cross tie:
 * the server joins two take placements. A cross-pane stroke that is not that
 * (a pull between two challenge rings, a stroke touching a non-take) is left with
 * its settled ink and no post — the surface did its gesture, the server has no
 * same-shaped act, exactly the quiet of a 204 (file 77 §2). NO geometry is sent.
 *
 * The /tie/cross answer is a plain proposal confirmation fragment, not field
 * markup, so it is NOT swapped into #field — swapping it would tear down the
 * split. The already-settled ink is the surface's acknowledgement; the faint
 * proposed line is drawn on each pane's overlay on its next read (file 77 §3).
 */
async function dispatchCrossTie(root: HTMLElement, recognised: Recognised): Promise<void> {
  // The cross-tie payload is the pure recognizer's job (crossTieFormBody): it
  // returns the {take×2, challenge×2} body when the stroke is a cross tie the
  // server can wire (a connect/carry whose two take ends span two different
  // challenges), or null otherwise — in which case the settled ink stays with no
  // post, the quiet of a 204 (file 77 §2).
  const body = crossTieFormBody(recognised, paneChallenges(root));
  if (!body) return;

  try {
    await fetch(`/tie/cross`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "HX-Request": "true",
      },
      body: body.toString(),
      credentials: "same-origin",
    });
  } catch {
    // A failed post leaves no mark — the ink has already settled/faded; there is
    // nothing to roll back (file 77 §2).
    return;
  }
  // Success (or a server no-op): leave the settled ink. The faint proposed cross
  // tie is drawn on each pane's overlay on its next read; we do NOT swap the
  // proposal confirmation into #field — that would tear down the split.
}

// ---- the public API: attach the recognizer to the mounted field ------------

/** A cleanup handle: detach the recognizer's listeners. */
export interface RecognizerHandle {
  detach(): void;
}

// The sample cadence while drawing (ms) — enough to shape the ink and feed the
// classifier without flooding the trace with points.
const SAMPLE_MS = 16;

/**
 * Attach the stroke recognizer to the field root. It captures pointer gestures
 * over the field, draws the ink, classifies on release through the pure
 * recognizer, and dispatches the recognised stroke. Returns a handle to detach.
 *
 * It co-exists with the existing HTMX affordances: a tap (quick, still) is not a
 * stroke (the classifier returns null), so taps still reach the HTMX links /
 * details toggles untouched — the recognizer only acts on a real drawn stroke.
 * A press that becomes a stroke is drawn as ink; a press on an interactive
 * control (a link, the composer, a state nav) is left alone so the control works.
 */
export function attachRecognizer(root: HTMLElement): RecognizerHandle {
  // The ink's animation mechanism is motion.ts now (Motion One) — no island-owned
  // keyframe stylesheet to inject. `doc` is still the hit-test document below.
  const doc = root.ownerDocument;

  let svg: SVGSVGElement | null = null;
  let ink: Ink | null = null;
  let points: TracePoint[] = [];
  let touched: TouchedObject[] = [];
  let crossedLine: Trace["crossedLine"] = null;
  let startTime = 0;
  let lastSample = 0;
  let activePointer: number | null = null;
  let carried = false;

  const seen = new Set<string>();

  const recordObjectAt = (clientX: number, clientY: number) => {
    const o = objectAt(doc, clientX, clientY);
    if (!o) return;
    const key = `${o.kind}:${o.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    touched.push(o);
  };

  const onDown = (e: PointerEvent) => {
    // Leave interactive controls alone — a press on a link, button, textarea or
    // summary is that control's, not a stroke (so HTMX / the composer work).
    const target = e.target as Element | null;
    if (target && target.closest("a, button, textarea, input, summary, [contenteditable]")) {
      return;
    }
    const liveSvg = root.querySelector<SVGSVGElement>("svg");
    if (!liveSvg) return;
    svg = liveSvg;
    activePointer = e.pointerId;
    startTime = e.timeStamp;
    lastSample = e.timeStamp;
    points = [toUserSpace(svg, e.clientX, e.clientY)];
    touched = [];
    seen.clear();
    crossedLine = lineAt(doc, e.clientX, e.clientY);
    // Carry intent: the press began ON a take (grabbing the object), which the
    // DOM knows and the classifier cannot infer from geometry. A drag that
    // starts on an object and moves it onto another is a carry; one that starts
    // in empty space and draws to an object is a connect (file 77 §1, §6).
    const originObj = objectAt(doc, e.clientX, e.clientY);
    carried = originObj?.kind === "take";
    recordObjectAt(e.clientX, e.clientY);
    ink = new Ink(svg);
    ink.begin(points[0]);
  };

  const onMove = (e: PointerEvent) => {
    if (activePointer === null || e.pointerId !== activePointer || !svg) return;
    if (e.timeStamp - lastSample < SAMPLE_MS) return;
    lastSample = e.timeStamp;
    points.push(toUserSpace(svg, e.clientX, e.clientY));
    recordObjectAt(e.clientX, e.clientY);
    if (!crossedLine) crossedLine = lineAt(doc, e.clientX, e.clientY);
    ink?.extend(points);
  };

  const onUp = (e: PointerEvent) => {
    if (activePointer === null || e.pointerId !== activePointer || !svg) return;
    activePointer = null;
    points.push(toUserSpace(svg, e.clientX, e.clientY));
    recordObjectAt(e.clientX, e.clientY);

    const trace: Trace = {
      points,
      durationMs: e.timeStamp - startTime,
      touched,
      crossedLine,
    };
    const recognised = classify(trace, { carried });

    // The ink settles to the logical shape (or fades on a no-match), then the
    // trace's geometry is discarded — only the meaning is kept (file 77 §5).
    ink?.settle(points, recognised);
    ink = null;

    if (recognised) void dispatch(root, recognised, crossedLine);

    // Discard the trace. Nothing geometric survives this call.
    points = [];
    touched = [];
    crossedLine = null;
    carried = false;
    seen.clear();
  };

  const onCancel = (e: PointerEvent) => {
    if (activePointer === null || e.pointerId !== activePointer) return;
    activePointer = null;
    ink?.abandon();
    ink = null;
    points = [];
    touched = [];
    crossedLine = null;
    carried = false;
    seen.clear();
  };

  root.addEventListener("pointerdown", onDown);
  root.addEventListener("pointermove", onMove);
  root.addEventListener("pointerup", onUp);
  root.addEventListener("pointercancel", onCancel);

  return {
    detach() {
      root.removeEventListener("pointerdown", onDown);
      root.removeEventListener("pointermove", onMove);
      root.removeEventListener("pointerup", onUp);
      root.removeEventListener("pointercancel", onCancel);
      ink?.abandon();
      ink = null;
    },
  };
}
